import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * ProductGallery — premium PDP main image with:
 *   • Magnifier zoom on hover (scale + follow-cursor)
 *   • Auto-cycle to next image when idle (not hovered / not zoomed)
 *   • Smooth fade crossfade between images
 *   • Prev / next arrows on hover
 *   • Thumbnail strip below
 *
 * Props:
 *   images: string[]    (product image URLs)
 *   alt: string         (product name)
 *   zoomLevel?: number  (default 1.7)
 *   idleMs?: number     (default 3200 — how often to auto-advance)
 */
const ProductGallery = ({ images, alt, zoomLevel = 1.7, idleMs = 3200 }) => {
  const list = images && images.length ? images : [""];
  const [idx, setIdx] = useState(0);
  const [hover, setHover] = useState(false);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const frameRef = useRef(null);

  // auto-cycle when idle
  useEffect(() => {
    if (hover || list.length <= 1) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % list.length), idleMs);
    return () => clearInterval(t);
  }, [hover, list.length, idleMs]);

  const onMove = (e) => {
    const r = frameRef.current?.getBoundingClientRect();
    if (!r) return;
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    setPos({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  };

  const next = (e) => { e?.stopPropagation?.(); setIdx((i) => (i + 1) % list.length); };
  const prev = (e) => { e?.stopPropagation?.(); setIdx((i) => (i - 1 + list.length) % list.length); };

  return (
    <div className="space-y-4" data-testid="product-gallery">
      {/* main frame */}
      <div
        ref={frameRef}
        className="relative aspect-square bg-white rounded-[24px] overflow-hidden shadow-sm border border-border/40 select-none"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onMouseMove={onMove}
        style={{ cursor: hover && list[idx] ? "zoom-in" : "default" }}
      >
        <AnimatePresence mode="wait">
          <motion.img
            key={idx}
            src={list[idx]}
            alt={`${alt} — view ${idx + 1}`}
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{
              opacity: 1,
              scale: hover ? zoomLevel : 1,
              transformOrigin: `${pos.x}% ${pos.y}%`,
            }}
            exit={{ opacity: 0 }}
            transition={{
              opacity: { duration: 0.45 },
              scale: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
              transformOrigin: { duration: 0 },
            }}
            className="w-full h-full object-cover will-change-transform"
            data-testid="product-main-image"
            draggable={false}
          />
        </AnimatePresence>

        {/* soft vignette on hover for premium feel */}
        <AnimatePresence>
          {hover && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "radial-gradient(circle at " +
                  pos.x + "% " + pos.y + "%, transparent 30%, rgba(61,49,64,0.05) 85%)",
              }}
            />
          )}
        </AnimatePresence>

        {/* arrows */}
        {list.length > 1 && (
          <>
            <motion.button
              onClick={prev}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: hover ? 1 : 0, x: hover ? 0 : -6 }}
              transition={{ duration: 0.3 }}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow flex items-center justify-center z-10"
              aria-label="previous"
              data-testid="gallery-prev"
            >
              <ChevronLeft size={18} />
            </motion.button>
            <motion.button
              onClick={next}
              initial={{ opacity: 0, x: 6 }}
              animate={{ opacity: hover ? 1 : 0, x: hover ? 0 : 6 }}
              transition={{ duration: 0.3 }}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow flex items-center justify-center z-10"
              aria-label="next"
              data-testid="gallery-next"
            >
              <ChevronRight size={18} />
            </motion.button>
          </>
        )}

        {/* image pagination dots */}
        {list.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
            {list.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                aria-label={`go to image ${i + 1}`}
                className="relative h-1.5 rounded-full overflow-hidden"
                style={{ width: i === idx ? 28 : 8 }}
              >
                <span
                  className="absolute inset-0 rounded-full transition-colors"
                  style={{
                    background: i === idx ? "rgba(61,49,64,0.85)" : "rgba(255,255,255,0.65)",
                    transition: "width 0.35s ease, background 0.35s",
                  }}
                />
              </button>
            ))}
          </div>
        )}

        {/* auto-play progress ring — only visible when not hovered */}
        {list.length > 1 && !hover && (
          <motion.div
            key={idx + "-progress"}
            aria-hidden
            className="absolute top-3 right-3 w-7 h-7 rounded-full"
            style={{
              background:
                "conic-gradient(from 0deg, rgba(201,120,154,0.9) var(--p, 0%), rgba(255,255,255,0.6) 0)",
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 1, 0] }}
            transition={{ duration: idleMs / 1000, ease: "linear" }}
          >
            <motion.span
              aria-hidden
              className="absolute inset-[3px] rounded-full bg-white/80 backdrop-blur flex items-center justify-center text-[9px] font-label uppercase tracking-[0.18em] text-charcoal"
            >
              {idx + 1}/{list.length}
            </motion.span>
          </motion.div>
        )}

        {/* hover hint */}
        <AnimatePresence>
          {hover && (
            <motion.div
              key="hint"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              className="absolute bottom-4 right-4 bg-charcoal/75 backdrop-blur text-cream font-label text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full pointer-events-none z-10"
            >
              Hover to zoom
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* thumbnails */}
      {list.length > 1 && (
        <div className="flex gap-3 overflow-x-auto pb-2 hide-scrollbar">
          {list.map((img, i) => (
            <motion.button
              key={i}
              onClick={() => setIdx(i)}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.96 }}
              className={`relative flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all duration-300 ${
                idx === i ? "border-rose shadow" : "border-transparent hover:border-border"
              }`}
              data-testid={`product-thumb-${i}`}
            >
              <img src={img} alt="" className="w-full h-full object-cover" />
            </motion.button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductGallery;
